// src/pages/Investigation.jsx
import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import {
  useInvestigation,
  DIMENSIONS_CONFIG,
  DIM_COLOR,
  EXPANSION_BADGE_COLORS,
} from '../hooks/useInvestigation';
import { apiService } from '../services/api';

// ─── Seed type config ─────────────────────────────────────────────────────────

const SEED_TYPES = [
  { id: 'sha256',         label: 'SHA256',          placeholder: '64-char SHA256…',                        mono: true  },
  { id: 'icon_hash',      label: 'Icon Hash',        placeholder: 'Perceptual hash (copy from analysis page)', mono: true  },
  { id: 'certificate',    label: 'Certificate',      placeholder: 'Certificate SHA256…',                   mono: true  },
  { id: 'cert_serial',    label: 'Cert Serial',      placeholder: 'Certificate serial number…',            mono: true  },
  { id: 'permission_set', label: 'Permission Set',   placeholder: 'Permission set hash…',                  mono: true  },
  { id: 'malware_family', label: 'Malware Family',   placeholder: 'e.g. Cerberus, BankBot, Vultur…',       mono: false },
  { id: 'artifact',       label: 'Artifact Hash',    placeholder: '64-char SHA256 of an extracted file…',  mono: true  },
  { id: 'receiver',       label: 'Receiver',         placeholder: 'com.example.MyReceiver',                mono: true  },
  { id: 'service',        label: 'Service',          placeholder: 'com.example.MyService',                 mono: true  },
  { id: 'intent_action',  label: 'Intent Action',    placeholder: 'android.intent.action.VIEW',            mono: true  },
];

// ─── Colour helpers ───────────────────────────────────────────────────────────

const FAMILY_COLORS = {
  Unknown:   '#546e7a',
  DroidBot:  '#ef5350',
  Klopatra:  '#ab47bc',
  Herodotus: '#ff9800',
  Cerberus:  '#26c6da',
};

const FAMILY_PALETTE = [
  '#6366f1', '#f97316', '#14b8a6', '#e11d48', '#84cc16',
  '#a855f7', '#0ea5e9', '#f43f5e', '#22d3ee', '#fb923c',
  '#4ade80', '#c084fc',
];

function familyColor(family) {
  if (!family || family === 'Unknown') return FAMILY_COLORS.Unknown;
  if (FAMILY_COLORS[family]) return FAMILY_COLORS[family];
  let hash = 0;
  for (let i = 0; i < family.length; i++) hash = (hash * 31 + family.charCodeAt(i)) >>> 0;
  return FAMILY_PALETTE[hash % FAMILY_PALETTE.length];
}

// ─── Quadratic Bézier path for multi-dimension parallel edges ─────────────────

function dimPath(d) {
  const s = d.source, t = d.target;
  const sx = s.x || 0, sy = s.y || 0;
  const tx = t.x || 0, ty = t.y || 0;
  const dx = tx - sx, dy = ty - sy;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const px = -dy / dist;  // unit perpendicular
  const py =  dx / dist;
  const SPACING = 12;
  const centerOffset = (d.dimIndex - (d.totalDims - 1) / 2) * SPACING;
  const arcHeight = Math.max(24, dist * 0.28) + centerOffset;
  const cx = (sx + tx) / 2 + px * arcHeight;
  const cy = (sy + ty) / 2 + py * arcHeight;
  return `M${sx},${sy} Q${cx},${cy} ${tx},${ty}`;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  page:  { minHeight: '100vh', background: '#f9fafb', display: 'flex', flexDirection: 'column' },
  body:  { flex: 1, display: 'flex', overflow: 'hidden', height: 'calc(100vh - 64px)' },
  left:  {
    width: 272, minWidth: 272, background: '#1f2937', color: '#f3f4f6',
    padding: '1.1rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem',
  },
  right: {
    width: 320, minWidth: 320, background: '#1f2937', color: '#f3f4f6',
    padding: '1.1rem', overflowY: 'auto', borderLeft: '1px solid #374151',
    display: 'flex', flexDirection: 'column', gap: '0.65rem',
  },
  canvas: { flex: 1, position: 'relative', overflow: 'hidden', background: '#111827' },
  svg:    { width: '100%', height: '100%' },

  sectionTitle: {
    fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: '#9ca3af', marginBottom: '0.35rem',
  },
  monoInput: {
    width: '100%', padding: '0.45rem 0.55rem', borderRadius: 6,
    border: '1px solid #374151', background: '#111827', color: '#f3f4f6',
    fontSize: '0.78rem', fontFamily: 'monospace', boxSizing: 'border-box',
  },
  numInput: {
    width: '100%', padding: '0.4rem 0.55rem', borderRadius: 6,
    border: '1px solid #374151', background: '#111827', color: '#f3f4f6',
    fontSize: '0.82rem', boxSizing: 'border-box',
  },
  dateInput: {
    width: '100%', padding: '0.4rem 0.55rem', borderRadius: 6,
    border: '1px solid #374151', background: '#111827', color: '#f3f4f6',
    fontSize: '0.78rem', boxSizing: 'border-box', colorScheme: 'dark',
  },
  slider: { width: '100%', accentColor: '#3b82f6', cursor: 'pointer' },
  checkRow: { display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.3rem', cursor: 'pointer' },
  dot: c => ({ width: 9, height: 9, borderRadius: '50%', background: c, flexShrink: 0 }),
  btn: (active, color = '#3b82f6') => ({
    width: '100%', padding: '0.55rem', borderRadius: 6, border: 'none',
    background: active ? color : '#374151',
    color: active ? 'white' : '#6b7280',
    fontWeight: 600, fontSize: '0.85rem',
    cursor: active ? 'pointer' : 'not-allowed', transition: 'background 0.2s',
  }),
  divider:  { borderTop: '1px solid #374151' },
  statsRow: { display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#9ca3af', flexWrap: 'wrap' },
  hint:     { fontSize: '0.68rem', color: '#6b7280', marginTop: '0.2rem' },

  emptyState: {
    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', color: '#4b5563',
    gap: '0.65rem', pointerEvents: 'none',
  },
  overlay: bg => ({
    position: 'absolute', inset: 0, background: bg, display: 'flex',
    alignItems: 'center', justifyContent: 'center', color: '#f3f4f6',
    fontSize: '0.95rem', fontWeight: 500,
  }),
  banner: (bg, fg) => ({
    position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
    background: bg, color: fg, borderRadius: 6, padding: '0.35rem 0.85rem',
    fontSize: '0.78rem', fontWeight: 500, whiteSpace: 'nowrap',
    boxShadow: '0 2px 8px rgba(0,0,0,0.4)', maxWidth: '80%', textAlign: 'center',
  }),

  card:      { background: '#111827', borderRadius: 7, padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  cardLabel: { fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' },
  cardValue: { fontSize: '0.82rem', wordBreak: 'break-all' },
  badge:     c => ({ display: 'inline-block', background: c + '22', color: c, border: `1px solid ${c}55`, borderRadius: 4, padding: '0.12rem 0.45rem', fontSize: '0.76rem', fontWeight: 600 }),
  tagChip:   { display: 'inline-block', background: '#312e81', color: '#a5b4fc', border: '1px solid #4338ca', borderRadius: 4, padding: '0.1rem 0.4rem', fontSize: '0.72rem', fontWeight: 500 },
  openBtn:   { display: 'block', textAlign: 'center', padding: '0.45rem', background: '#3b82f6', color: 'white', borderRadius: 6, textDecoration: 'none', fontWeight: 600, fontSize: '0.82rem' },
  ghostBtn:  (color = '#374151') => ({ display: 'block', width: '100%', textAlign: 'center', padding: '0.45rem', background: color, color: color === '#374151' ? '#d1d5db' : 'white', borderRadius: 6, border: 'none', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }),

  connTypeRow:  { display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0', cursor: 'pointer', userSelect: 'none' },
  connTypeLbl:  { fontSize: '0.8rem', fontWeight: 600, flex: 1 },
  connCount:    { fontSize: '0.72rem', color: '#6b7280', marginRight: 2 },
  chevron:      open => ({ fontSize: '0.65rem', color: '#6b7280', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }),
  valueRow:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.2rem 0.5rem 0.2rem 1.4rem', borderRadius: 4, cursor: 'pointer', fontSize: '0.72rem', fontFamily: 'monospace', color: '#9ca3af', wordBreak: 'break-all', lineHeight: 1.4 },

  popupBackdrop: { position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  popup:         { background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: '1.25rem', width: 420, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: '0.75rem', boxShadow: '0 8px 32px rgba(0,0,0,0.6)' },
  popupTitle:    { fontWeight: 700, fontSize: '0.92rem', color: '#f3f4f6' },
  popupValueBox: { background: '#111827', borderRadius: 6, padding: '0.65rem 0.75rem', fontFamily: 'monospace', fontSize: '0.78rem', color: '#d1d5db', wordBreak: 'break-all', lineHeight: 1.5 },
  popupActions:  { display: 'flex', gap: '0.5rem' },
  popupBtn:      color => ({ flex: 1, padding: '0.4rem 0.65rem', borderRadius: 6, border: 'none', background: color, color: 'white', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }),

  selectInput: {
    width: '100%', padding: '0.4rem 0.55rem', borderRadius: 6,
    border: '1px solid #374151', background: '#111827', color: '#f3f4f6',
    fontSize: '0.82rem', boxSizing: 'border-box',
  },
  searchBtn: active => ({
    width: '100%', padding: '0.4rem', borderRadius: 6, border: 'none',
    background: active ? '#1d4ed8' : '#374151',
    color: active ? 'white' : '#6b7280',
    fontSize: '0.82rem', fontWeight: 600,
    cursor: active ? 'pointer' : 'not-allowed',
  }),
  resultRow: selected => ({
    padding: '0.4rem 0.5rem', borderRadius: 5, cursor: 'pointer',
    background: selected ? '#1e3a5f' : '#111827',
    border: `1px solid ${selected ? '#3b82f6' : '#374151'}`,
    marginBottom: '0.22rem', transition: 'border-color 0.12s',
  }),
  selectedSeedBadge: {
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.35rem 0.5rem', borderRadius: 5,
    background: '#052e16', border: '1px solid #166534',
    fontSize: '0.75rem', color: '#4ade80',
  },

  ctxMenu:    { position: 'fixed', background: '#1f2937', border: '1px solid #374151', borderRadius: 8, zIndex: 2000, minWidth: 180, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.6)' },
  ctxItem:    (danger) => ({ display: 'block', width: '100%', textAlign: 'left', padding: '0.55rem 0.9rem', border: 'none', background: 'none', color: danger ? '#f87171' : '#f3f4f6', fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'none', boxSizing: 'border-box' }),
  ctxDivider: { borderTop: '1px solid #374151', margin: '0.2rem 0' },

  tooltip: {
    position: 'fixed', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 7,
    padding: '0.55rem 0.75rem', zIndex: 3000, pointerEvents: 'none', maxWidth: 240,
    boxShadow: '0 4px 16px rgba(0,0,0,0.6)', fontSize: '0.78rem', color: '#f1f5f9',
  },
};

// ─── ConnectionsSection ───────────────────────────────────────────────────────

function ConnectionsSection({ connections, onValueClick }) {
  const [openTypes, setOpenTypes] = useState({});
  const toggle = type => setOpenTypes(p => ({ ...p, [type]: !p[type] }));

  if (!connections?.length) return null;

  return (
    <div style={S.card}>
      <div style={S.cardLabel}>Connections</div>
      {connections.map(({ type, count, shared_values }) => {
        const dim = DIMENSIONS_CONFIG.find(d => d.name === type);
        const open = !!openTypes[type];
        const vals = shared_values || [];
        const hiddenCount = count - vals.length;
        return (
          <div key={type}>
            <div style={S.connTypeRow} onClick={() => toggle(type)}>
              <span style={S.dot(dim?.color || '#9ca3af')} />
              <span style={S.connTypeLbl}>{dim?.label || type}</span>
              <span style={S.connCount}>×{count}</span>
              <span style={S.chevron(open)}>▶</span>
            </div>
            {open && (
              <>
                {vals.map(v => (
                  <div
                    key={v} style={S.valueRow} title={v}
                    onClick={() => onValueClick(type, v)}
                    onMouseEnter={e => { e.currentTarget.style.background = '#1f2937'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ flex: 1 }}>{v.length > 52 ? v.slice(0, 50) + '…' : v}</span>
                    <span style={{ color: '#4b5563', fontSize: '0.65rem', marginLeft: 4, flexShrink: 0 }}>ⓘ</span>
                  </div>
                ))}
                {hiddenCount > 0 && (
                  <div style={{ ...S.valueRow, color: '#6b7280', cursor: 'default', fontStyle: 'italic', fontSize: '0.68rem' }}>
                    +{hiddenCount} more…
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── ValuePopup ──────────────────────────────────────────────────────────────

function ValuePopup({ type, value, onClose }) {
  const dim = DIMENSIONS_CONFIG.find(d => d.name === type);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div style={S.popupBackdrop} onClick={onClose}>
      <div style={S.popup} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {dim && <span style={S.dot(dim.color)} />}
          <span style={S.popupTitle}>{dim ? dim.label : type} — Shared Value</span>
        </div>
        <div style={S.popupValueBox}>{value}</div>
        <div style={S.popupActions}>
          <button style={S.popupBtn(copied ? '#059669' : '#374151')} onClick={copy}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button style={S.popupBtn('#6b7280')} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Investigation() {
  const {
    graphData, loading, error,
    selectedNode, setSelectedNode,
    hiddenEdgeTypes, toggleEdgeType, forceEnableDims,
    buildGraph, resetFilters,
    expandedNodes, expandNode, collapseNode,
    nodeGenerationRef, generationCounterRef, expansionSourceRef,
  } = useInvestigation();

  const svgRef           = useRef(null);
  const simulationRef    = useRef(null);
  const nodePositionsRef = useRef({});
  const autoBuiltRef     = useRef(false);
  const pickerDragRef    = useRef(null); // { lastX, lastY } while dragging
  const [searchParams]   = useSearchParams();

  // ── Build parameters ──
  const [seedType, setSeedType]           = useState('sha256');
  const [seedValue, setSeedValue]         = useState('');
  const [resolvedSha256, setResolvedSha256] = useState('');
  const [resolvedLabel, setResolvedLabel]   = useState('');
  const [lookupResults, setLookupResults]   = useState(null);
  const [lookupLoading, setLookupLoading]   = useState(false);

  const [checkedPivots, setCheckedPivots] = useState(() => new Set());
  const [limitPerPivot, setLimitPerPivot] = useState(20);
  const [firstSeenAfter, setFirstSeenAfter]   = useState('');
  const [firstSeenBefore, setFirstSeenBefore] = useState('');

  // ── Client-side filters ──
  const [pkgPrefix, setPkgPrefix]   = useState('');
  const [minShared, setMinShared]   = useState(1);
  const [minPerType, setMinPerType] = useState({});
  const [requiredTags, setRequiredTags] = useState(new Set());

  // ── UI state ──
  const [valuePopup, setValuePopup]     = useState(null);
  const [contextMenu, setContextMenu]   = useState(null);
  const [tooltip, setTooltip]           = useState({ visible: false, x: 0, y: 0, lines: [] });
  // Expand picker: { node, x, y } while open
  const [expandPicker, setExpandPicker]     = useState(null);
  const [pickerSelected, setPickerSelected] = useState(new Set());
  const [lastExpandDims, setLastExpandDims] = useState(new Set());

  // Pre-fill seed from ?sha256= param; clear dimensions per spec
  useEffect(() => {
    if (autoBuiltRef.current) return;
    const sha = searchParams.get('sha256');
    if (!sha || sha.length !== 64) return;
    autoBuiltRef.current = true;
    setSeedType('sha256');
    setSeedValue(sha);
    setCheckedPivots(new Set());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSeedTypeChange = newType => {
    setSeedType(newType);
    setSeedValue('');
    setResolvedSha256('');
    setResolvedLabel('');
    setLookupResults(null);
  };

  const handleTogglePivot = name =>
    setCheckedPivots(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });

  const handleToggleTag = tag =>
    setRequiredTags(prev => { const n = new Set(prev); n.has(tag) ? n.delete(tag) : n.add(tag); return n; });

  const makeDateFilters = () => {
    const f = {};
    if (firstSeenAfter)  f.first_seen_after  = firstSeenAfter;
    if (firstSeenBefore) f.first_seen_before = firstSeenBefore;
    return f;
  };

  // Resolve non-SHA256 seeds
  const handleLookup = async () => {
    const val = seedValue.trim();
    if (!val) return;
    setLookupLoading(true);
    setLookupResults(null);
    setResolvedSha256('');
    setResolvedLabel('');
    try {
      const data = await apiService.resolveSeed(seedType, val, 20);
      const results = data?.results || [];
      setLookupResults(results);
      // Auto-select if exactly one result; auto-build per spec
      if (results.length === 1) {
        const r = results[0];
        const sha = r.id || r.sha256;
        const label = r.app_name || sha.slice(0, 12) + '…';
        setResolvedSha256(sha);
        setResolvedLabel(label);
        if (checkedPivots.size > 0) {
          nodePositionsRef.current = {};
          buildGraph(sha, [...checkedPivots], limitPerPivot, makeDateFilters());
        }
      }
    } catch {
      setLookupResults([]);
    } finally {
      setLookupLoading(false);
    }
  };

  const handlePickResult = result => {
    const sha = result.id || result.sha256;
    setResolvedSha256(sha);
    setResolvedLabel(result.app_name || sha.slice(0, 12) + '…');
  };

  const effectiveSha = seedType === 'sha256' ? seedValue.trim() : resolvedSha256;

  const handleBuild = useCallback(() => {
    if (!effectiveSha || checkedPivots.size === 0) return;
    nodePositionsRef.current = {};
    buildGraph(effectiveSha, [...checkedPivots], limitPerPivot, makeDateFilters());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSha, checkedPivots, limitPerPivot, firstSeenAfter, firstSeenBefore, buildGraph]);

  const handleUseAsSeed = sha256 => {
    setSeedType('sha256');
    setSeedValue(sha256);
    setResolvedSha256('');
    setResolvedLabel('');
    setLookupResults(null);
    setSelectedNode(null);
    setContextMenu(null);
    // Auto-build from new seed
    if (checkedPivots.size > 0) {
      nodePositionsRef.current = {};
      buildGraph(sha256, [...checkedPivots], limitPerPivot, makeDateFilters());
    }
  };

  // dimsOverride: string[] from the picker; falls back to checkedPivots
  const handleExpandNode = (node, dimsOverride) => {
    const dims = dimsOverride ?? [...checkedPivots];
    setContextMenu(null);
    setExpandPicker(null);
    const sha = node.id || node.sha256;
    expandNode(sha, dims, limitPerPivot, makeDateFilters());
    // Force-enable used dimensions so new edges are not immediately filtered out
    forceEnableDims(dims);
  };

  const handleCollapseNode = node => {
    setContextMenu(null);
    const sha = node.id || node.sha256;
    collapseNode(sha);
    if (selectedNode?.id === sha) setSelectedNode(null);
  };

  const dismissContextMenu = () => setContextMenu(null);

  // Reads contextMenu position BEFORE closing the menu
  const openExpandPicker = (node, menuX, menuY) => {
    const px = Math.max(8, Math.min(menuX, window.innerWidth  - 238));
    const py = Math.max(8, Math.min(menuY, window.innerHeight - 430));
    setContextMenu(null);
    // Pre-select lastExpandDims if set, otherwise all unchecked per spec
    setPickerSelected(lastExpandDims.size > 0 ? new Set(lastExpandDims) : new Set());
    setExpandPicker({ node, x: px, y: py });
  };

  const handlePickerDragStart = useCallback(e => {
    e.preventDefault();
    pickerDragRef.current = { lastX: e.clientX, lastY: e.clientY };
    const onMove = me => {
      const dx = me.clientX - pickerDragRef.current.lastX;
      const dy = me.clientY - pickerDragRef.current.lastY;
      pickerDragRef.current.lastX = me.clientX;
      pickerDragRef.current.lastY = me.clientY;
      setExpandPicker(prev => prev ? { ...prev, x: prev.x + dx, y: prev.y + dy } : null);
    };
    const onUp = () => {
      pickerDragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // ── D3 render ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!graphData || !svgRef.current) return;
    if (simulationRef.current) simulationRef.current.stop();

    const svgEl = svgRef.current;
    const { width, height } = svgEl.getBoundingClientRect();
    const svg = d3.select(svgEl);
    svg.selectAll('*').remove();

    const g = svg.append('g');
    svg.call(d3.zoom().scaleExtent([0.1, 4]).on('zoom', ev => g.attr('transform', ev.transform)));

    const seedId = graphData.nodes.find(n => n.is_seed)?.id;

    // ── 1. Package/tag filter ───────────────────────────────────────────────
    const prefix = pkgPrefix.trim().toLowerCase();
    const allowedShas = new Set([seedId]);
    graphData.nodes.forEach(n => {
      if (n.is_seed) return;
      if (prefix && !(n.package_name || '').toLowerCase().startsWith(prefix)) return;
      if (requiredTags.size > 0 && !(n.tags || []).some(t => requiredTags.has(t))) return;
      allowedShas.add(n.id);
    });

    // ── 2. Edge dimension filter ────────────────────────────────────────────
    // Each edge is one pair; visible if enough of its dimensions pass.
    const edgesWithDims = graphData.edges
      .filter(e => allowedShas.has(e.source) && allowedShas.has(e.target))
      .map(e => ({
        ...e,
        _visibleDims: (e.dimensions || []).filter(dim =>
          !hiddenEdgeTypes.has(dim.type) &&
          (dim.count ?? 1) >= (minPerType[dim.type] ?? 1)
        ),
      }))
      .filter(e => e._visibleDims.length >= minShared);

    // ── 3. Visible node IDs ────────────────────────────────────────────────
    const visibleIds = new Set([seedId]);
    edgesWithDims.forEach(e => { visibleIds.add(e.source); visibleIds.add(e.target); });

    // ── 4. Sim nodes — restore saved positions ─────────────────────────────
    const saved = nodePositionsRef.current;
    const simNodes = graphData.nodes
      .filter(n => visibleIds.has(n.id))
      .map(n => {
        const pos = saved[n.id];
        return pos ? { ...n, x: pos.x, y: pos.y } : { ...n };
      });
    const nodeIdx = Object.fromEntries(simNodes.map(n => [n.id, n]));

    // ── 5. Resolve edge source/target to node objects ─────────────────────
    const simEdges = edgesWithDims
      .map(e => ({ ...e, source: nodeIdx[e.source], target: nodeIdx[e.target] }))
      .filter(e => e.source && e.target);

    // ── 6. Explode into per-dimension rendering items ──────────────────────
    const dimItems = [];
    simEdges.forEach(edge => {
      edge._visibleDims.forEach((dim, i) => {
        dimItems.push({
          source:   edge.source,
          target:   edge.target,
          dim,
          dimIndex: i,
          totalDims: edge._visibleDims.length,
          _pairKey: [edge.source.id, edge.target.id].sort().join('|'),
        });
      });
    });

    // ── 7. Force simulation ────────────────────────────────────────────────
    const sim = d3.forceSimulation(simNodes)
      .force('link',      d3.forceLink(simEdges).id(d => d.id).distance(130))
      .force('charge',    d3.forceManyBody().strength(-350))
      .force('center',    d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide(44));
    simulationRef.current = sim;

    // ── 8. Draw edges ──────────────────────────────────────────────────────
    const link = g.append('g').attr('class', 'links')
      .selectAll('path').data(dimItems).join('path')
      .attr('fill', 'none')
      .attr('stroke', d => DIM_COLOR[d.dim.type] || '#6b7280')
      .attr('stroke-width', 1.8)
      .attr('stroke-opacity', 0.6)
      .attr('stroke-linecap', 'round')
      .on('mouseover', (ev, d) => {
        const hk = d._pairKey;
        link.attr('stroke-opacity', dl => dl._pairKey === hk ? 1.0 : 0.05);
        const src = d.source, tgt = d.target;
        const srcName = src.app_name || src.id?.slice(0, 12) + '…';
        const tgtName = tgt.app_name || tgt.id?.slice(0, 12) + '…';
        const lines = [
          `${srcName} ↔ ${tgtName}`,
          ...d.dim ? [`${DIMENSIONS_CONFIG.find(c => c.name === d.dim.type)?.label || d.dim.type}: ×${d.dim.count}`] : [],
        ];
        setTooltip({ visible: true, x: ev.clientX, y: ev.clientY, lines });
      })
      .on('mousemove', ev => setTooltip(t => ({ ...t, x: ev.clientX, y: ev.clientY })))
      .on('mouseout', () => {
        link.attr('stroke-opacity', 0.6);
        setTooltip(t => ({ ...t, visible: false }));
      });

    // ── 9. Draw nodes ──────────────────────────────────────────────────────
    const nodeG = g.append('g').attr('class', 'nodes')
      .selectAll('g').data(simNodes).join('g')
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        setContextMenu(null);
        // Aggregate connections across all edges touching this node
        const dimMap = new Map();
        graphData.edges
          .filter(e => e.source === d.id || e.target === d.id)
          .forEach(e => {
            (e.dimensions || []).forEach(dim => {
              if (!dimMap.has(dim.type)) {
                dimMap.set(dim.type, { type: dim.type, count: dim.count, shared_values: [...(dim.shared_values || [])] });
              } else {
                const ex = dimMap.get(dim.type);
                ex.count = Math.max(ex.count, dim.count);
                (dim.shared_values || []).forEach(v => { if (!ex.shared_values.includes(v)) ex.shared_values.push(v); });
              }
            });
          });
        setSelectedNode({ ...d, _connections: [...dimMap.values()] });
      })
      .on('contextmenu', (event, d) => {
        event.preventDefault(); event.stopPropagation();
        setSelectedNode({ ...d });
        setContextMenu({ node: d, x: event.clientX, y: event.clientY });
      })
      .on('mouseover', (ev, d) => {
        const connectedIds = new Set([d.id]);
        simEdges.forEach(e => {
          if (e.source.id === d.id || e.target.id === d.id) {
            connectedIds.add(e.source.id);
            connectedIds.add(e.target.id);
          }
        });
        nodeG.attr('opacity', n => connectedIds.has(n.id) ? 1 : 0.15);
        link.attr('stroke-opacity', dl =>
          dl.source.id === d.id || dl.target.id === d.id ? 1.0 : 0.05
        );
        const step = nodeGenerationRef.current.get(d.id);
        const lines = [
          d.app_name || d.id?.slice(0, 12) + '…',
          d.malware_family && d.malware_family !== 'Unknown' ? d.malware_family : null,
          step > 0 ? `Expansion #${step}` : (d.is_seed ? 'Seed node' : null),
        ].filter(Boolean);
        setTooltip({ visible: true, x: ev.clientX, y: ev.clientY, lines });
      })
      .on('mousemove', ev => setTooltip(t => ({ ...t, x: ev.clientX, y: ev.clientY })))
      .on('mouseout', () => {
        nodeG.attr('opacity', 1);
        link.attr('stroke-opacity', 0.6);
        setTooltip(t => ({ ...t, visible: false }));
      })
      .call(
        d3.drag()
          .on('start', (ev, d) => { if (!ev.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
          .on('drag',  (ev, d) => { d.fx = ev.x; d.fy = ev.y; })
          .on('end',   (ev, d) => { if (!ev.active) sim.alphaTarget(0); d.fx = null; d.fy = null; })
      );

    const isSeed = d => d.id === seedId;
    const isExpanded = d => expandedNodes.has(d.id);
    const nodeRadius = d => isSeed(d) ? 28 : 18;

    // Layer 1: outer glow ring
    nodeG.append('circle')
      .attr('r', d => nodeRadius(d) + 5)
      .attr('fill', 'none')
      .attr('stroke', d => familyColor(d.malware_family))
      .attr('stroke-width', 1.5)
      .attr('opacity', 0.18)
      .attr('pointer-events', 'none');

    // Layer 2: main circle
    nodeG.append('circle')
      .attr('r', nodeRadius)
      .attr('fill', d => familyColor(d.malware_family))
      .attr('fill-opacity', 0.73)
      .attr('stroke', d => isSeed(d) ? '#60a5fa' : '#1f2937')
      .attr('stroke-width', d => isSeed(d) ? 3 : 1.5);

    // Layer 3: star on seed
    nodeG.filter(isSeed)
      .append('text').text('★')
      .attr('text-anchor', 'middle').attr('dy', '0.35em')
      .attr('font-size', '14px').attr('fill', 'white')
      .style('pointer-events', 'none');

    // Layer 4: dashed cyan ring — expanded FROM indicator
    nodeG.filter(d => isExpanded(d) && !isSeed(d))
      .append('circle')
      .attr('r', d => nodeRadius(d) + 9)
      .attr('fill', 'none')
      .attr('stroke', 'rgba(0,240,255,0.75)')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '5,3')
      .attr('pointer-events', 'none');

    // Layer 5: expansion badge (step >= 1, age < 3)
    nodeG.each(function(d) {
      const step = nodeGenerationRef.current.get(d.id);
      if (!step || step === 0) return;
      const age = generationCounterRef.current - step;
      if (age >= 3) return;
      const opacity = [0.95, 0.55, 0.25][age];
      const letter = String.fromCharCode(65 + (step - 1) % 26);
      const color  = EXPANSION_BADGE_COLORS[(step - 1) % EXPANSION_BADGE_COLORS.length];
      const r = nodeRadius(d);
      const badgeG = d3.select(this).append('g')
        .attr('transform', `translate(${r * 0.72},${-r * 0.72})`)
        .attr('opacity', opacity)
        .style('pointer-events', 'none');
      badgeG.append('circle').attr('r', 7).attr('fill', color);
      badgeG.append('text')
        .text(letter)
        .attr('text-anchor', 'middle').attr('dy', '0.35em')
        .attr('font-size', '8px').attr('font-weight', '700').attr('fill', 'white');
    });

    // Layer 6: label below node
    nodeG.append('text')
      .text(d => (d.app_name || d.id?.slice(0, 8) || '').slice(0, 18))
      .attr('text-anchor', 'middle')
      .attr('dy', d => nodeRadius(d) + 13)
      .attr('font-size', '9px').attr('fill', '#d1d5db')
      .style('pointer-events', 'none');

    // ── 10. Tick ───────────────────────────────────────────────────────────
    sim.on('tick', () => {
      simNodes.forEach(n => { nodePositionsRef.current[n.id] = { x: n.x, y: n.y }; });
      link.attr('d', dimPath);
      nodeG.attr('transform', d => `translate(${d.x || 0},${d.y || 0})`);
    });

    svg.on('click', () => { setSelectedNode(null); setContextMenu(null); });

    return () => sim.stop();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphData, hiddenEdgeTypes, pkgPrefix, minShared, minPerType, requiredTags, expandedNodes]);

  // ── Derived stats ──────────────────────────────────────────────────────────
  const canBuild  = effectiveSha.length === 64 && checkedPivots.size > 0 && !loading;
  const showGraph = graphData !== null || loading || error;

  const seedId = graphData?.nodes.find(n => n.is_seed)?.id;

  const allTags = graphData
    ? [...new Set(graphData.nodes.flatMap(n => n.tags || []))].sort()
    : [];

  // Visible stats (mirrors D3 filter logic, for footer counters)
  const prefix = pkgPrefix.trim().toLowerCase();
  const statsAllowed = new Set([seedId].filter(Boolean));
  if (graphData) {
    graphData.nodes.forEach(n => {
      if (n.is_seed) return;
      if (prefix && !(n.package_name || '').toLowerCase().startsWith(prefix)) return;
      if (requiredTags.size > 0 && !(n.tags || []).some(t => requiredTags.has(t))) return;
      statsAllowed.add(n.id);
    });
  }
  const visibleEdges = graphData
    ? graphData.edges
        .filter(e => statsAllowed.has(e.source) && statsAllowed.has(e.target))
        .filter(e => {
          const passing = (e.dimensions || []).filter(dim =>
            !hiddenEdgeTypes.has(dim.type) && (dim.count ?? 1) >= (minPerType[dim.type] ?? 1)
          );
          return passing.length >= minShared;
        })
    : [];
  const visibleNodeIds = new Set([seedId].filter(Boolean));
  visibleEdges.forEach(e => { visibleNodeIds.add(e.source); visibleNodeIds.add(e.target); });

  // Distinct dimension types present in graph (for global slider max)
  const graphDimTypeCount = graphData
    ? new Set(graphData.edges.flatMap(e => (e.dimensions || []).map(d => d.type))).size
    : 1;

  // Per-type max count (for slider bounds)
  const typeMaxCount = {};
  if (graphData) {
    graphData.edges.forEach(e => {
      (e.dimensions || []).forEach(dim => {
        typeMaxCount[dim.type] = Math.max(typeMaxCount[dim.type] || 1, dim.count ?? 1);
      });
    });
  }

  // Unique dimension types present in current graph
  const presentDimTypes = graphData
    ? [...new Set(graphData.edges.flatMap(e => (e.dimensions || []).map(d => d.type)))]
    : [];

  const isFiltered = hiddenEdgeTypes.size > 0 || prefix || requiredTags.size > 0
    || minShared > 1 || Object.values(minPerType).some(v => v > 1);

  return (
    <div style={S.page} onClick={dismissContextMenu}>
      <Navbar />
      <div style={S.body}>

        {/* ── LEFT PANEL ──────────────────────────────────────────────── */}
        <div style={S.left} onClick={e => e.stopPropagation()}>

          <div>
            <div style={S.sectionTitle}>Seed Type</div>
            <select style={S.selectInput} value={seedType} onChange={e => handleSeedTypeChange(e.target.value)}>
              {SEED_TYPES.map(st => <option key={st.id} value={st.id}>{st.label}</option>)}
            </select>
          </div>

          <div>
            <div style={S.sectionTitle}>{SEED_TYPES.find(s => s.id === seedType)?.label || 'Value'}</div>
            <input
              style={SEED_TYPES.find(s => s.id === seedType)?.mono ? S.monoInput : S.numInput}
              placeholder={SEED_TYPES.find(s => s.id === seedType)?.placeholder || ''}
              value={seedValue}
              onChange={e => {
                setSeedValue(e.target.value);
                if (seedType !== 'sha256') { setResolvedSha256(''); setResolvedLabel(''); setLookupResults(null); }
              }}
              onKeyDown={e => {
                if (e.key !== 'Enter') return;
                if (seedType === 'sha256') { canBuild && handleBuild(); }
                else { seedValue.trim() && handleLookup(); }
              }}
              spellCheck={false}
            />
          </div>

          {seedType !== 'sha256' && (
            <button style={S.searchBtn(!!seedValue.trim() && !lookupLoading)} onClick={handleLookup} disabled={!seedValue.trim() || lookupLoading}>
              {lookupLoading ? 'Searching…' : 'Search APKs'}
            </button>
          )}

          {lookupResults !== null && (
            <div>
              {lookupResults.length === 0 ? (
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textAlign: 'center', padding: '0.5rem' }}>
                  No APKs found for this value.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginBottom: '0.3rem' }}>
                    {lookupResults.length} result{lookupResults.length > 1 ? 's' : ''} — pick seed APK:
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {lookupResults.map(r => {
                      const rId = r.id || r.sha256;
                      const picked = resolvedSha256 === rId;
                      return (
                        <div key={rId} style={S.resultRow(picked)} onClick={() => handlePickResult(r)}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: picked ? '#93c5fd' : '#f3f4f6', marginBottom: '0.1rem' }}>
                            {(r.app_name || 'Unknown').slice(0, 24)}
                          </div>
                          <div style={{ fontSize: '0.68rem', color: '#9ca3af', fontFamily: 'monospace' }}>
                            {(r.package_name || '').slice(0, 28)}
                          </div>
                          <div style={{ fontSize: '0.65rem', color: '#6b7280', fontFamily: 'monospace' }}>
                            {rId.slice(0, 16)}…
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {seedType !== 'sha256' && resolvedSha256 && (
            <div style={S.selectedSeedBadge}>
              <span style={{ fontWeight: 700 }}>✓</span>
              <span style={{ flex: 1, fontSize: '0.75rem' }}>Seed: <strong>{resolvedLabel}</strong></span>
              <button style={{ background: 'none', border: 'none', color: '#4ade80', cursor: 'pointer', fontSize: '0.75rem' }}
                onClick={() => { setResolvedSha256(''); setResolvedLabel(''); }} title="Clear selection">✕</button>
            </div>
          )}

          <div>
            <div style={S.sectionTitle}>Pivot Dimensions</div>
            {DIMENSIONS_CONFIG.map(dim => (
              <label key={dim.name} style={S.checkRow}>
                <input type="checkbox" checked={checkedPivots.has(dim.name)} onChange={() => handleTogglePivot(dim.name)} />
                <span style={S.dot(dim.color)} />
                <span style={{ fontSize: '0.8rem' }}>{dim.label}</span>
              </label>
            ))}
          </div>

          <div>
            <div style={S.sectionTitle}>Results per pivot</div>
            <input type="number" style={S.numInput} value={limitPerPivot} min={1} max={100}
              onChange={e => setLimitPerPivot(Number(e.target.value))} />
          </div>

          <div>
            <div style={S.sectionTitle}>First Seen — After</div>
            <input type="date" style={S.dateInput} value={firstSeenAfter} onChange={e => setFirstSeenAfter(e.target.value)} />
          </div>
          <div>
            <div style={S.sectionTitle}>First Seen — Before</div>
            <input type="date" style={S.dateInput} value={firstSeenBefore} onChange={e => setFirstSeenBefore(e.target.value)} />
          </div>

          <button style={S.btn(canBuild)} onClick={handleBuild} disabled={!canBuild}>
            {loading ? 'Working…' : 'Build Graph'}
          </button>

          {/* ── Post-build filters ───────────────────────────────────── */}
          {graphData && (
            <>
              <div style={S.divider} />

              <div>
                <div style={S.sectionTitle}>Package Prefix Filter</div>
                <input style={S.monoInput} placeholder="com.example…"
                  value={pkgPrefix} onChange={e => setPkgPrefix(e.target.value)} />
                <div style={S.hint}>Hides nodes whose package doesn't start with this. Seed always kept.</div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                  <div style={S.sectionTitle}>Min. Shared Dimensions</div>
                  <span style={{ fontSize: '0.78rem', color: '#60a5fa', fontWeight: 700 }}>{minShared}</span>
                </div>
                <input type="range" min={1} max={Math.max(graphDimTypeCount, 1)} step={1}
                  value={Math.min(minShared, graphDimTypeCount)}
                  onChange={e => setMinShared(Number(e.target.value))}
                  style={S.slider}
                />
                <div style={S.hint}>
                  Hide pairs sharing fewer than {minShared} distinct dimension{minShared > 1 ? 's' : ''} (max {graphDimTypeCount}).
                </div>
              </div>

              {presentDimTypes.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.45rem' }}>
                    <div style={S.sectionTitle}>Edge Visibility</div>
                    {(hiddenEdgeTypes.size > 0 || Object.values(minPerType).some(v => v > 1)) && (
                      <button onClick={() => { resetFilters(); setMinPerType({}); setMinShared(1); }}
                        style={{ fontSize: '0.68rem', color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        Reset all
                      </button>
                    )}
                  </div>
                  {DIMENSIONS_CONFIG.filter(dim => presentDimTypes.includes(dim.name)).map(dim => {
                    const enabled  = !hiddenEdgeTypes.has(dim.name);
                    const observed = typeMaxCount[dim.name] || 1;
                    const minVal   = minPerType[dim.name] ?? 1;
                    const isHighCard = dim.name === 'service' || dim.name === 'receiver' || dim.name === 'intent_action';
                    const showSlider = enabled && isHighCard;

                    return (
                      <div key={dim.name} style={{ opacity: enabled ? 1 : 0.35, marginBottom: '0.5rem' }}>
                        <label style={{ ...S.checkRow, marginBottom: showSlider ? '0.2rem' : 0 }}>
                          <input type="checkbox" checked={enabled} onChange={() => toggleEdgeType(dim.name)} />
                          <span style={S.dot(dim.color)} />
                          <span style={{ fontSize: '0.8rem', flex: 1 }}>{dim.label}</span>
                          {enabled && minVal > 1 && (
                            <span style={{ fontSize: '0.68rem', color: '#60a5fa', fontWeight: 700, marginLeft: 4 }}>≥{minVal}</span>
                          )}
                        </label>
                        {showSlider && (
                          <div style={{ paddingLeft: '1.4rem' }}>
                            <input type="range" min={1} max={Math.max(observed, 5)} step={1}
                              value={minVal}
                              onChange={e => setMinPerType(prev => ({ ...prev, [dim.name]: Number(e.target.value) }))}
                              style={S.slider}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#6b7280', marginTop: '0.1rem' }}>
                              <span>min {minVal}</span>
                              <span style={{ color: observed < minVal ? '#f59e0b' : '#6b7280' }}>
                                {observed < minVal ? 'none visible' : `max ${observed}`}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {allTags.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                    <div style={S.sectionTitle}>Tag Filter</div>
                    {requiredTags.size > 0 && (
                      <button onClick={() => setRequiredTags(new Set())} style={{ fontSize: '0.68rem', color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        Clear
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                    {allTags.map(tag => (
                      <label key={tag} style={{ ...S.checkRow, opacity: requiredTags.size > 0 && !requiredTags.has(tag) ? 0.45 : 1 }}>
                        <input type="checkbox" checked={requiredTags.has(tag)} onChange={() => handleToggleTag(tag)} />
                        <span style={{ fontSize: '0.78rem', fontFamily: 'monospace', color: '#a5b4fc' }}>{tag}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div style={S.statsRow}>
                <span>{visibleNodeIds.size} nodes</span>
                <span>{visibleEdges.length} edges</span>
                <span>{expandedNodes.size} expanded</span>
                {isFiltered && <span style={{ color: '#f59e0b' }}>filtered</span>}
              </div>
            </>
          )}
        </div>

        {/* ── CANVAS ──────────────────────────────────────────────────── */}
        <div style={S.canvas}>
          <svg ref={svgRef} style={S.svg} />
          {!showGraph && (
            <div style={S.emptyState}>
              <div style={{ fontSize: '2.5rem', opacity: 0.35 }}>⬡</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 500 }}>No graph yet</div>
              <div style={{ fontSize: '0.82rem', opacity: 0.65 }}>Choose a seed type, enter a value, pick pivot dimensions, then click Build Graph</div>
            </div>
          )}
          {loading && <div style={S.overlay('rgba(17,24,39,0.75)')}>Working…</div>}
          {error && !loading && <div style={S.banner('#7f1d1d', '#fca5a5')}>{error}</div>}
          {graphData?.truncated && !loading && (
            <div style={S.banner('#92400e', '#fef3c7')}>Graph truncated — reduce limit or deselect pivot types</div>
          )}
        </div>

        {/* ── RIGHT PANEL ─────────────────────────────────────────────── */}
        {selectedNode && (
          <div style={S.right} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', borderBottom: '1px solid #374151', paddingBottom: '0.5rem' }}>
              Node Detail
            </div>

            {selectedNode.icon && (
              <img src={`data:image/png;base64,${selectedNode.icon}`} alt="icon"
                style={{ width: 52, height: 52, borderRadius: 8, alignSelf: 'center', border: '2px solid #374151' }} />
            )}

            {selectedNode.is_seed && (
              <div style={{ fontSize: '0.72rem', color: '#60a5fa', fontWeight: 600 }}>★ Seed node</div>
            )}
            {expandedNodes.has(selectedNode.id) && !selectedNode.is_seed && (
              <div style={{ fontSize: '0.72rem', color: '#60a5fa' }}>⊕ Expanded FROM this node</div>
            )}
            {(() => {
              const step = nodeGenerationRef.current.get(selectedNode.id);
              if (!step || step === 0) return null;
              const src = expansionSourceRef.current.get(step);
              return (
                <div style={{ fontSize: '0.72rem', color: '#a3a3a3' }}>
                  Discovered in expansion #{step}{src ? ` · from ${src.name}` : ''}
                </div>
              );
            })()}

            <div style={S.card}>
              <div style={S.cardLabel}>App Name</div>
              <div style={S.cardValue}>{selectedNode.app_name || '—'}</div>
            </div>
            <div style={S.card}>
              <div style={S.cardLabel}>Package</div>
              <div style={S.cardValue}>{selectedNode.package_name || '—'}</div>
            </div>
            <div style={S.card}>
              <div style={S.cardLabel}>SHA256</div>
              <div style={{ ...S.cardValue, fontFamily: 'monospace', fontSize: '0.7rem' }}>{selectedNode.id}</div>
            </div>
            <div style={S.card}>
              <div style={S.cardLabel}>Malware Family</div>
              <span style={S.badge(familyColor(selectedNode.malware_family))}>
                {selectedNode.malware_family || 'Unknown'}
              </span>
            </div>
            {(selectedNode.first_seen || selectedNode.first_upload_time) && (
              <div style={S.card}>
                <div style={S.cardLabel}>First Seen</div>
                <div style={S.cardValue}>
                  {new Date(selectedNode.first_seen || selectedNode.first_upload_time).toLocaleString()}
                </div>
              </div>
            )}
            {(selectedNode.last_analyzed || selectedNode.timestamp) && (
              <div style={S.card}>
                <div style={S.cardLabel}>Last Analyzed</div>
                <div style={S.cardValue}>
                  {new Date(selectedNode.last_analyzed || selectedNode.timestamp).toLocaleString()}
                </div>
              </div>
            )}
            {selectedNode.uploader && (
              <div style={S.card}>
                <div style={S.cardLabel}>Uploaded By</div>
                <div style={S.cardValue}>{selectedNode.uploader}</div>
              </div>
            )}
            {selectedNode.tags && selectedNode.tags.length > 0 && (
              <div style={S.card}>
                <div style={S.cardLabel}>Tags</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.2rem' }}>
                  {selectedNode.tags.map(t => <span key={t} style={S.tagChip}>{t}</span>)}
                </div>
              </div>
            )}

            <ConnectionsSection
              connections={selectedNode._connections || []}
              onValueClick={(type, value) => setValuePopup({ type, value })}
            />

            {!selectedNode.is_seed && (
              expandedNodes.has(selectedNode.id) ? (
                <button style={S.ghostBtn('#7f1d1d')} onClick={() => handleCollapseNode(selectedNode)}
                  title="Remove nodes/edges introduced by this expansion (cascades)">
                  ⊖ Collapse Expansion
                </button>
              ) : (
                <button style={S.ghostBtn('#1d4ed8')} disabled={loading}
                  title="Choose dimensions and expand neighbors of this node"
                  onClick={() => {
                    const px = Math.max(8, Math.min(window.innerWidth  - 238, window.innerWidth  / 2));
                    const py = Math.max(8, Math.min(window.innerHeight - 430, window.innerHeight / 2 - 100));
                    setPickerSelected(lastExpandDims.size > 0 ? new Set(lastExpandDims) : new Set());
                    setExpandPicker({ node: selectedNode, x: px, y: py });
                  }}>
                  ⊕ Expand Node…
                </button>
              )
            )}

            <button style={S.ghostBtn()} onClick={() => handleUseAsSeed(selectedNode.id)}>
              Use as Seed
            </button>
            <Link to={`/analysis/${selectedNode.id}`} style={S.openBtn}>
              Open Analysis →
            </Link>
          </div>
        )}
      </div>

      {/* ── CONTEXT MENU ────────────────────────────────────────────── */}
      {contextMenu && (
        <div style={{ ...S.ctxMenu, left: contextMenu.x, top: contextMenu.y }}
          onClick={e => e.stopPropagation()}>
          {!contextMenu.node.is_seed && (
            expandedNodes.has(contextMenu.node.id) ? (
              <button style={S.ctxItem(true)} onClick={() => handleCollapseNode(contextMenu.node)}>
                ⊖ Collapse Expansion
              </button>
            ) : (
              <button style={S.ctxItem(false)} onClick={() => openExpandPicker(contextMenu.node, contextMenu.x, contextMenu.y)} disabled={loading}>
                ⊕ Expand Node…
              </button>
            )
          )}
          <div style={S.ctxDivider} />
          <button style={S.ctxItem(false)} onClick={() => handleUseAsSeed(contextMenu.node.id)}>
            ⟳ Use as Seed
          </button>
          <Link to={`/analysis/${contextMenu.node.id}`}
            style={{ ...S.ctxItem(false), display: 'block' }}
            onClick={() => setContextMenu(null)}>
            → Open Analysis
          </Link>
          <div style={S.ctxDivider} />
          <button style={S.ctxItem(false)} onClick={() => setContextMenu(null)}>Cancel</button>
        </div>
      )}

      {/* ── EXPAND PICKER ───────────────────────────────────────────── */}
      {expandPicker && (
        <div
          style={{
            position: 'fixed', left: expandPicker.x, top: expandPicker.y,
            zIndex: 2100, background: '#0a0f1a',
            border: '1px solid rgba(0,240,255,0.25)', borderRadius: 8,
            width: 222, boxShadow: '0 8px 28px rgba(0,0,0,0.8)',
            display: 'flex', flexDirection: 'column',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Draggable title bar */}
          <div
            style={{
              padding: '0.55rem 0.9rem 0.4rem', fontWeight: 700, fontSize: '0.65rem',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color: 'rgba(0,240,255,0.75)', borderBottom: '1px solid rgba(255,255,255,0.07)',
              cursor: 'grab', userSelect: 'none',
            }}
            onMouseDown={handlePickerDragStart}
          >
            ⊕ Expand Dimensions
          </div>

          {/* Dimension checkboxes */}
          <div style={{ padding: '0.3rem 0', maxHeight: 300, overflowY: 'auto' }}>
            {DIMENSIONS_CONFIG.map(dim => (
              <label key={dim.name} style={{
                display: 'flex', alignItems: 'center', gap: '0.55rem',
                padding: '0.35rem 0.9rem', cursor: 'pointer',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,240,255,0.07)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <input
                  type="checkbox"
                  checked={pickerSelected.has(dim.name)}
                  style={{ accentColor: '#00f0ff', width: 12, height: 12, cursor: 'pointer', flexShrink: 0 }}
                  onChange={() => setPickerSelected(prev => {
                    const n = new Set(prev);
                    n.has(dim.name) ? n.delete(dim.name) : n.add(dim.name);
                    return n;
                  })}
                />
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: dim.color, flexShrink: 0 }} />
                <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', fontFamily: 'monospace' }}>
                  {dim.label}
                </span>
              </label>
            ))}
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex', gap: '0.4rem',
            padding: '0.45rem 0.9rem 0.55rem',
            borderTop: '1px solid rgba(255,255,255,0.07)',
          }}>
            <button
              style={{
                flex: 1, padding: '0.35rem 0', borderRadius: 6, cursor: 'pointer',
                background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem', fontWeight: 600,
              }}
              onClick={() => setExpandPicker(null)}
            >
              Cancel
            </button>
            <button
              style={{
                flex: 1, padding: '0.35rem 0', borderRadius: 6,
                background: pickerSelected.size > 0 ? 'rgba(0,240,255,0.10)' : 'transparent',
                border: `1px solid ${pickerSelected.size > 0 ? 'rgba(0,240,255,0.35)' : 'rgba(255,255,255,0.1)'}`,
                color: pickerSelected.size > 0 ? '#00f0ff' : 'rgba(255,255,255,0.25)',
                fontSize: '0.68rem', fontWeight: 600,
                cursor: pickerSelected.size > 0 ? 'pointer' : 'not-allowed',
              }}
              disabled={pickerSelected.size === 0}
              onClick={() => {
                const dims = [...pickerSelected];
                setLastExpandDims(new Set(pickerSelected));
                handleExpandNode(expandPicker.node, dims);
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      )}

      {/* ── HOVER TOOLTIP ───────────────────────────────────────────── */}
      {tooltip.visible && tooltip.lines.length > 0 && (
        <div style={{ ...S.tooltip, left: tooltip.x + 14, top: tooltip.y + 14 }}>
          {tooltip.lines.map((l, i) => (
            <div key={i} style={{ opacity: i === 0 ? 1 : 0.7, fontSize: i === 0 ? '0.82rem' : '0.74rem' }}>{l}</div>
          ))}
        </div>
      )}

      {/* ── VALUE POPUP ─────────────────────────────────────────────── */}
      {valuePopup && (
        <ValuePopup type={valuePopup.type} value={valuePopup.value} onClose={() => setValuePopup(null)} />
      )}
    </div>
  );
}
